import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import reportService from '../services/reportService';
import caseService from '../services/caseService';
import inferenceService from '../services/inferenceService';
import '../styles/ReportPage.css';

export default function ReportPage() {
  const { caseId } = useParams();
  const [blobUrl,   setBlobUrl]   = useState(null);
  const [caseData,  setCaseData]  = useState(null);
  const [result,    setResult]    = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [err,       setErr]       = useState(null);
  const [errFix,    setErrFix]    = useState(null);
  const blobRef    = useRef(null);
  const mountedRef = useRef(true);
  const navigate   = useNavigate();

  // ── Cleanup blob URLs on unmount ─────────────────────────────
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (blobRef.current && blobRef.current.startsWith('blob:')) {
        URL.revokeObjectURL(blobRef.current);
        blobRef.current = null;
      }
    };
  }, []);

  // ── Reset state fully when caseId changes ────────────────────
  useEffect(() => {
    setBlobUrl(null);
    setCaseData(null);
    setResult(null);
    setErr(null);
    setErrFix(null);

    if (blobRef.current && blobRef.current.startsWith('blob:')) {
      URL.revokeObjectURL(blobRef.current);
      blobRef.current = null;
    }
  }, [caseId]);

  const loadReport = useCallback(async () => {
    if (!caseId) return;
    setLoading(true);
    setErr(null);
    setErrFix(null);
    setBlobUrl(null);

    try {
      const [url, cData, res] = await Promise.all([
        reportService.getPdfBlobUrl(caseId),
        caseService.getById(caseId),
        inferenceService.getResult(caseId).catch(() => null),
      ]);

      if (!mountedRef.current) return;

      if (blobRef.current && blobRef.current.startsWith('blob:')) {
        URL.revokeObjectURL(blobRef.current);
      }
      blobRef.current = url;

      setBlobUrl(url);
      setCaseData(cData);
      setResult(res);

    } catch (e) {
      if (!mountedRef.current) return;
      const d = e.response?.data;
      setErr(d?.error || d?.detail || 'Report not available');
      setErrFix(d?.fix || (Array.isArray(d?.traceback) ? d.traceback.join(' ') : null));
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [caseId]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const parts = dateStr.split('-');
      if (parts.length === 3 && parts[0].length === 4) {
        return `${parts[2]}-${parts[1]}-${parts[0]}`;
      }
    } catch (e) {}
    return dateStr;
  };

  const handleDownload = async () => {
    if (!blobUrl) return;
    try {
      const patientName = caseData?.patient_name || 'Patient';
      const patientId = caseData?.patient_id_display || caseId;
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `AI_Report_${patientName}_${patientId}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (error) {
      alert('Failed to download PDF.');
    }
  };

  if (loading) {
    return (
      <div className="rep__container rep__loading-container">
        <div className="rep__loading-wrapper">
          <i className="fa-solid fa-spinner fa-spin fa-2x rep__loading-spinner" />
          <div className="rep__loading-text">Loading report…</div>
        </div>
      </div>
    );
  }

  if (err && !loading) {
    return (
      <div className="rep__container rep__error-container">
        <div className="rep__error-wrapper">
          <i className="fa-solid fa-file-circle-xmark fa-2x rep__error-icon" />
          <div className="rep__error-title">Report Unavailable</div>
          <div className="rep__error-message">{err}</div>
          {errFix && <div className="rep__error-fix">{errFix}</div>}
          <div className="rep__error-actions">
            <button className="btn-outline-pink" onClick={loadReport}>
              <i className="fa-solid fa-rotate-right" />Retry
            </button>
            <button className="btn-outline-pink" onClick={() => navigate('/patients')}>
              <i className="fa-solid fa-arrow-left" />Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rep__container">
      <div className="rep__sidebar">
        <div className="rep__sidebar-header">
          <div>
            <div className="rep__sidebar-label">Report Details</div>
          </div>
        </div>

        <div className="rep__sidebar-content">
          <div className="rep__section-block">
            <div className="rep__section-heading">Patient Details</div>
            <div className="rep__patient-card">
              <div className="rep__patient-row">
                <div className="rep__patient-label">Patient Id</div>
                <div className="rep__patient-value">
                  {caseData?.patient?.patient_id || caseData?.patient_id_display || caseData?.patient_id || '—'}
                </div>
              </div>
              <div className="rep__patient-row">
                <div className="rep__patient-label">Patient Name</div>
                <div className="rep__patient-value">
                  {caseData?.patient?.full_name || caseData?.patient_name || '—'}
                </div>
              </div>
              <div className="rep__patient-row">
                <div className="rep__patient-label">Patient Age</div>
                <div className="rep__patient-value">
                  {caseData?.patient?.age || caseData?.patient_age || '—'}
                </div>
              </div>
              <div className="rep__patient-row">
                <div className="rep__patient-label">Date</div>
                <div className="rep__patient-value">
                  {formatDate(caseData?.scan_date) || '—'}
                </div>
              </div>
            </div>
          </div>

          <div className="rep__section-block">
            <div className="rep__section-heading">AI Output:</div>
            <div className="rep__ai-card">
              <div className="rep__ai-breast-sec">
                <h3 className="rep__breast-title">Right Breast:</h3>
                <ul className="rep__bullet-list">
                  <li className="rep__bullet-item">
                    Predicted Result: {result?.right?.label || result?.label || 'N/A'}
                  </li>
                  <li className="rep__bullet-item">
                    BI-RADS Category: {result?.right?.birads_score || result?.birads_score || 'N/A'}
                  </li>
                </ul>
              </div>
              <div className="rep__ai-breast-sec">
                <h3 className="rep__breast-title">Left Breast:</h3>
                <ul className="rep__bullet-list">
                  <li className="rep__bullet-item">
                    Predicted Result: {result?.left?.label || result?.label || 'N/A'}
                  </li>
                  <li className="rep__bullet-item">
                    BI-RADS Category: {result?.left?.birads_score || result?.birads_score || 'N/A'}
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="rep__main-pane">
        <div className="rep__main-header">
          <div className="rep__main-title">AI Report Preview</div>
          <button
            className="rep__download-btn"
            onClick={handleDownload}
            disabled={!blobUrl}
          >
            Download
          </button>
        </div>

        <div className="rep__pdf-container">
          {blobUrl ? (
            <iframe
              key={blobUrl}
              scrolling="no"
              src={`${blobUrl}#toolbar=0&navpanes=0&scrollbar=0&zoom=page-width`}
              title="Report PDF"
              className="rep__pdf-preview"
            />
          ) : (
            <div className="rep__empty-state">
              <div className="rep__empty-title">Report preview not available</div>
              <div className="rep__empty-text">Use the download button to save the PDF.</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}